/**
 * Módulo centralizado de Analytics para E-commerce
 * Soporta: Facebook Pixel + Google Analytics 4 (gtag)
 */

import { getFbc } from "@/lib/fbclid";
import { ensureFbp } from "@/lib/fbp";
// Links de marcas: el paso "tocó Comprar" del que entró con ?ref=. Ver lib/reflink.js.
import { anotarRef } from "@/lib/reflink";

// ============================================================================
// PRODUCTOS - Catálogo centralizado
// Los `prices` son SOLO fallback si falla el fetch de /api/prices/matrix; el
// precio real se pasa siempre por dynamicPrice. Sincronizados con prod (RW=USD,
// AR=ARS) el 2026-07-04. Si cambian los precios en la DB, actualizar acá también.
// ============================================================================
export const PRODUCTS = {
  armonia: {
    id: "1",
    name: "Armonía Ilustrada",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 31900, USD: 27 },
  },
  modos: {
    id: "11",
    name: "Modos Ilustrados",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 27900, USD: 24 },
  },
  armonia2: {
    id: "21",
    name: "Armonía Ilustrada 2",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 27900, USD: 24 },
  },
  tensiones: {
    id: "31",
    name: "Tensiones Ilustradas",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 31900, USD: 27 },
  },
  glue: {
    id: "41",
    name: "Glue Mágico para Guitarristas",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 31900, USD: 27 },
  },
  arreglos: {
    id: "51",
    name: "Arreglos Ilustrados",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 31900, USD: 27 },
  },
  // Escalas Ilustradas (id 61). Sin esta entrada, trackAddToCart('escalas') hace
  // early-return y el pixel de AddToCart se pierde.
  // El value real se pasa por dynamicPrice desde buyBook; estos son fallback.
  escalas: {
    id: "61",
    name: "Escalas Ilustradas",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 17900, USD: 15 },
  },
  // Musica Ilustrada de Cero (id 71) — en PREVENTA en los siete idiomas. Mismo
  // motivo que Escalas: sin esta entrada trackAddToCart("musicacero") corta y se
  // pierde el evento. El precio real viaja por dynamicPrice; el USD 15 de aca es
  // el fallback, y coincide con products.price del 71.
  musicacero: {
    id: "71",
    name: "Música Ilustrada de Cero",
    category: "Libros/Teoría Musical",
    brand: "Brian Calli Music",
    prices: { ARS: 17900, USD: 15 },
  },
  clarinet: {
    id: "1001",
    name: "Pack Clarinete",
    category: "Packs",
    brand: "Brian Calli Music",
    prices: { ARS: 20900, USD: 15 },
  },
};

// Helper para obtener precio según moneda
export const getPrice = (productKey, currency) => {
  const product = PRODUCTS[productKey];
  return product?.prices?.[currency] ?? product?.prices?.USD ?? 0;
};

// ============================================================================
// FACEBOOK PIXEL
// ============================================================================

// Token de page view: se mantiene mientras no cambie la URL y cambia al navegar.
// Los event_id del embudo alto se armaban con Date.now(), así que un remount del
// componente o un doble click producían dos ids distintos y Meta contaba dos
// eventos: eso es lo que venía inflando ViewContent y AddToCart. Con el token,
// dos disparos de la misma vista comparten id y Meta los deduplica; una vista
// nueva de verdad sigue contando aparte.
// NO se toca el id de InitiateCheckout (bc_) ni el de Purchase (pur_): esos
// viajan al backend y son la clave de dedup contra el CAPI del webhook.
let pvPath = null;
let pvToken = null;
const pageViewToken = () => {
  try {
    const path =
      typeof window !== "undefined" && window.location ? window.location.pathname : "";
    if (path !== pvPath || !pvToken) {
      pvPath = path;
      pvToken = Math.random().toString(36).slice(2, 10);
    }
    return pvToken;
  } catch {
    return "0";
  }
};

const trackFB = (eventName, params, eventId) => {
  if (typeof window === "undefined" || typeof fbq !== "function") return;
  try {
    fbq("track", eventName, params, eventId ? { eventID: eventId } : undefined);
  } catch (e) {
    console.warn("[FB Pixel]", e);
  }
};

// Envío server-side a Conversions API (usa API route de Next.js).
// Usa navigator.sendBeacon cuando está disponible: sobrevive navegaciones/redirects
// y no pasa por fetch (evita recursión Sentry ↔ FB Pixel SDK en iOS).
// Fallback: setTimeout(0) + fetch para browsers sin sendBeacon.
const sendFBServer = (eventName, eventId, customData) => {
  try {
    // ensureFbp y no getCookie("_fbp"): si el pixel está bloqueado (o todavía no bajó)
    // la cookie no existe y el evento salía sin browser id.
    const fbp = ensureFbp();
    // getFbc y no getCookie("_fbc"): si el pixel está bloqueado o ITP le comió la
    // cookie, cae a la que persistimos nosotros desde el ?fbclid= de la URL.
    const fbc = getFbc();
    const body = JSON.stringify({ eventName, eventId, customData, user: { fbp, fbc } });

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const queued = navigator.sendBeacon("/api/fb-events", new Blob([body], { type: "application/json" }));
      if (queued) return;
    }

    // Fallback: setTimeout para desacoplar del call stack síncrono (Sentry/iOS)
    setTimeout(() => {
      fetch("/api/fb-events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      }).catch((err) => console.error(`[FB CAPI] ${eventName}`, err));
    }, 0);
  } catch (err) {
    console.error(`[FB CAPI] ${eventName}`, err);
  }
};

// ============================================================================
// GOOGLE ANALYTICS 4 (gtag)
// ============================================================================
const trackGA4 = (eventName, params) => {
  if (typeof window === "undefined" || typeof gtag !== "function") return;
  try {
    gtag("event", eventName, params);
  } catch (e) {
    console.warn("[GA4]", e);
  }
};

// ============================================================================
// GOOGLE ADS (conversiones)
// El tag AW-… se configura en components/analytics/GoogleAdsTag.jsx; acá solo se
// disparan las conversiones (send_to = AW-XXX/label). Sin env vars es un no-op:
// los labels existen recién cuando se crean las conversion actions en la cuenta.
// ============================================================================
const GADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
const GADS_LABELS = {
  purchase: process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_PURCHASE,
  begin_checkout: process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_BEGIN_CHECKOUT,
};

/**
 * Conversión de Google Ads.
 * @param {"purchase"|"begin_checkout"} labelKey
 * @param {{value?: number, currency?: string, transactionId?: string, email?: string}} [data]
 *   email: enhanced conversions — gtag lo normaliza y hashea antes de enviarlo
 *   (requiere allow_enhanced_conversions en el config del tag).
 */
export const trackGoogleAdsConversion = (labelKey, { value, currency, transactionId, email } = {}) => {
  if (typeof window === "undefined" || typeof gtag !== "function") return;
  const label = GADS_LABELS[labelKey];
  if (!GADS_ID || !label) return;
  try {
    if (email) {
      gtag("set", "user_data", { email: String(email).trim().toLowerCase() });
    }
    const params = { send_to: `${GADS_ID}/${label}` };
    if (value != null) params.value = value;
    if (currency) params.currency = currency;
    if (transactionId) params.transaction_id = transactionId;
    gtag("event", "conversion", params);
  } catch (e) {
    console.warn("[Google Ads]", e);
  }
};

// ============================================================================
// EVENTOS DE E-COMMERCE
// ============================================================================

/**
 * view_item_list - Usuario ve la lista de productos (landing /libros)
 */
export const trackViewItemList = (currency = "USD") => {
  const eventId = `vil_${pageViewToken()}`;
  const items = Object.entries(PRODUCTS).map(([key, p], idx) => ({
    item_id: p.id,
    item_name: p.name,
    item_category: p.category,
    item_brand: p.brand,
    price: p.prices[currency] ?? p.prices.USD,
    index: idx,
  }));

  // GA4
  trackGA4("view_item_list", {
    item_list_id: "libros_landing",
    item_list_name: "Libros - Landing Page",
    items,
  });

  // FB Pixel
  const fbParams = {
    content_type: "product_group",
    content_ids: Object.values(PRODUCTS).map((p) => p.id),
    contents: items.map((i) => ({ id: i.item_id, quantity: 1 })),
  };
  trackFB("ViewContent", fbParams, eventId);
  sendFBServer("ViewContent", eventId, fbParams);
};

/**
 * view_item - Usuario ve un producto específico (interactúa con tabs, galería)
 * @param {string} productKey - key en PRODUCTS (armonia, modos, etc.)
 * @param {string} currency
 * @param {number} [dynamicPrice] - precio dinámico del API (override del hardcoded)
 */
export const trackViewItem = (productKey, currency = "USD", dynamicPrice) => {
  const product = PRODUCTS[productKey];
  if (!product) return;

  const price = dynamicPrice ?? product.prices[currency] ?? product.prices.USD;
  const eventId = `vi_${product.id}_${pageViewToken()}`;

  // GA4
  trackGA4("view_item", {
    currency,
    value: price,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price,
    }],
  });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: [product.id],
    content_name: product.name,
    content_category: "Libros de Música",
    value: price,
    currency,
  };
  trackFB("ViewContent", fbParams, eventId);
  sendFBServer("ViewContent", eventId, fbParams);
};

/**
 * view_item (pack) - Usuario ve la página de un pack/bundle
 * @param {string} slug - pack slug (panorama, futuro, etc.)
 * @param {string} name - display name ("Pack Panorama")
 * @param {number} value - precio total del pack
 * @param {string} currency
 */
export const trackViewPack = (slug, name, value, currency = "USD") => {
  const contentId = `pack_${slug}`;
  const eventId = `vp_${slug}_${pageViewToken()}`;

  // GA4
  trackGA4("view_item", {
    currency,
    value,
    items: [{
      item_id: contentId,
      item_name: name,
      item_category: "Packs",
      item_brand: "Brian Calli Music",
      price: value,
    }],
  });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: [contentId],
    content_name: name,
    content_category: "Packs",
    value,
    currency,
  };
  trackFB("ViewContent", fbParams, eventId);
  sendFBServer("ViewContent", eventId, fbParams);
};

/**
 * add_to_cart - Usuario hace click en "Comprar"
 * @param {string} productKey - key en PRODUCTS (armonia, modos, etc.)
 * @param {string} currency
 * @param {number} [dynamicPrice] - precio dinámico del API (override del hardcoded)
 */
export const trackAddToCart = async (productKey, currency = "USD", dynamicPrice) => {
  // Antes del return por producto desconocido: para Links de marcas lo que cuenta es que
  // tocó Comprar, aunque el producto no esté en el catálogo del pixel.
  anotarRef("carrito");
  const product = PRODUCTS[productKey];
  if (!product) return;

  const price = dynamicPrice ?? product.prices[currency] ?? product.prices.USD;
  const eventId = `atc_${product.id}_${pageViewToken()}`;

  // GA4
  trackGA4("add_to_cart", {
    currency,
    value: price,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price,
      quantity: 1,
    }],
  });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: [product.id],
    content_name: product.name,
    value: price,
    currency,
  };
  trackFB("AddToCart", fbParams, eventId);
  await sendFBServer("AddToCart", eventId, fbParams);
};

/**
 * add_to_cart (pack) - Usuario hace click en "Comprar" un pack/bundle
 * @param {string} slug - pack slug (panorama, futuro, etc.)
 * @param {string} name - display name ("Pack Panorama")
 * @param {number} value - precio total del pack
 * @param {string} currency
 */
export const trackAddToCartPack = async (slug, name, value, currency = "USD") => {
  anotarRef("carrito");
  const contentId = `pack_${slug}`;
  const eventId = `atc_${slug}_${pageViewToken()}`;

  // GA4
  trackGA4("add_to_cart", {
    currency,
    value,
    items: [{
      item_id: contentId,
      item_name: name,
      item_category: "Packs",
      item_brand: "Brian Calli Music",
      price: value,
      quantity: 1,
    }],
  });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: [contentId],
    content_name: name,
    value,
    currency,
  };
  trackFB("AddToCart", fbParams, eventId);
  await sendFBServer("AddToCart", eventId, fbParams);
};

/**
 * begin_checkout - Usuario inicia el checkout
 */
export const trackBeginCheckout = (productKeys = [], currency = "USD") => {
  const eventId = `bc_${Date.now()}`;
  const items = productKeys
    .map((key) => PRODUCTS[key])
    .filter(Boolean)
    .map((p) => ({
      item_id: p.id,
      item_name: p.name,
      item_category: p.category,
      item_brand: p.brand,
      price: p.prices[currency] ?? p.prices.USD,
      quantity: 1,
    }));

  const value = items.reduce((sum, i) => sum + i.price, 0);

  // GA4
  trackGA4("begin_checkout", {
    currency,
    value,
    items,
  });

  // Google Ads (secundaria, observación)
  trackGoogleAdsConversion("begin_checkout", { value, currency });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: items.map((i) => i.item_id),
    contents: items.map((i) => ({ id: i.item_id, quantity: 1 })),
    value,
    currency,
    num_items: items.length,
  };
  trackFB("InitiateCheckout", fbParams, eventId);
  sendFBServer("InitiateCheckout", eventId, fbParams);
};

/**
 * purchase - Compra completada
 * @param {string} [email] - para enhanced conversions de Google Ads
 */
export const trackPurchase = async (productKeys = [], currency = "USD", transactionId = null, email = null) => {
  const eventId = `pur_${Date.now()}`;
  const items = productKeys
    .map((key) => PRODUCTS[key])
    .filter(Boolean)
    .map((p) => ({
      item_id: p.id,
      item_name: p.name,
      item_category: p.category,
      item_brand: p.brand,
      price: p.prices[currency] ?? p.prices.USD,
      quantity: 1,
    }));

  const value = items.reduce((sum, i) => sum + i.price, 0);

  // GA4
  trackGA4("purchase", {
    transaction_id: transactionId || eventId,
    currency,
    value,
    items,
  });

  // Google Ads (primaria)
  trackGoogleAdsConversion("purchase", {
    value,
    currency,
    transactionId: transactionId || eventId,
    email,
  });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: items.map((i) => i.item_id),
    contents: items.map((i) => ({ id: i.item_id, quantity: 1 })),
    value,
    currency,
    num_items: items.length,
  };
  trackFB("Purchase", fbParams, eventId);
  await sendFBServer("Purchase", eventId, fbParams);
};

/**
 * lead - Click en compra de libro físico (sale de la web a BuscaLibre/MercadoLibre)
 * @param {string} productKey - key en PRODUCTS (armonia, modos, etc.)
 * @param {string} currency
 * @param {number} [dynamicPrice] - precio dinámico del API
 * @param {string} [destination] - URL o nombre del destino externo
 */
export const trackLead = (productKey, currency = "USD", dynamicPrice, destination) => {
  const product = PRODUCTS[productKey];
  if (!product) return;

  const price = dynamicPrice ?? product.prices[currency] ?? product.prices.USD;
  const eventId = `lead_${product.id}_${Date.now()}`;

  // GA4
  trackGA4("generate_lead", {
    currency,
    value: price,
    lead_source: destination || "physical_book",
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_category: product.category,
      item_brand: product.brand,
      price,
    }],
  });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: [product.id],
    content_name: product.name,
    content_category: "Libros Físicos",
    value: price,
    currency,
  };
  trackFB("Lead", fbParams, eventId);
  sendFBServer("Lead", eventId, fbParams);
};

/**
 * begin_checkout (custom) - Para checkout con items arbitrarios (ej: suscripciones)
 * @param {Array} items - [{id, name, price, quantity}]
 * @param {number} value - valor total
 * @param {string} currency
 * @param {object} [extra] - params adicionales para FB (checkout_source, etc.)
 */
export const trackBeginCheckoutCustom = (items, value, currency = "USD", extra = {}) => {
  const eventId = `bc_${Date.now()}`;

  // GA4 (transport_type beacon: sobrevive redirecciones a Stripe/MercadoPago)
  trackGA4("begin_checkout", {
    currency,
    value,
    items: items.map((i) => ({
      item_id: i.id,
      item_name: i.name,
      price: i.price,
      quantity: i.quantity || 1,
    })),
    transport_type: "beacon",
  });

  // Google Ads (secundaria, observación)
  trackGoogleAdsConversion("begin_checkout", { value, currency });

  // FB Pixel
  const fbParams = {
    content_type: "product",
    content_ids: items.map((i) => i.id),
    contents: items.map((i) => ({ id: i.id, quantity: i.quantity || 1, item_price: i.price })),
    value,
    currency,
    num_items: items.length,
    ...extra,
  };
  trackFB("InitiateCheckout", fbParams, eventId);
  sendFBServer("InitiateCheckout", eventId, fbParams);

  return eventId;
};

// ============================================================================
// HELPER GENÉRICO — dispara un evento GA4 desde cualquier página
// Replica el patrón `sendGA` de las apps legacy (public/app/*)
// ============================================================================
export const sendGA = (eventName, params) => {
  if (typeof window === "undefined") return;
  try {
    if (typeof gtag === "function") gtag("event", eventName, params || {});
  } catch (e) {
    /* silently ignore */
  }
};

// ============================================================================
// WHATSAPP — click en la burbuja flotante
// Sólo pixel de browser: `Contact` no está en EVENTOS_PERMITIDOS de
// lib/fbEventsGuard.js, así que no se manda por CAPI. Es el único lado del
// canal que se puede medir sin datos de terceros (del otro lado sólo queda el
// `(ref: ...)` que viaja en el texto prellenado), así que conviene no sacarlo.
// ============================================================================
export const trackWhatsappClick = (source = "web") => {
  trackGA4("whatsapp_click", { source });
  trackFB("Contact", { content_category: "whatsapp", content_name: source });
};
